import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {hideToast} from '../../redux/actions';
import {selectToastState} from "../../redux/reducers/privateStateReducer/selectors";
import './Toast.css'
import {EngravedIcon} from "../gameUi/EngravedIcon";
import {MaskedIcon} from "../coloredIcons/MaskedIcon/MaskedIcon";
import {REGISTRY_SINGLETON} from "../../scenes/Registry";
import {TOAST_ICONS, TOAST_TYPES} from "../../constants/toast";
import {usePrev} from "../../hooks/react/usePrev";
import {selectCurrentPlayer, selectPhase} from "../../redux/reducers/sharedStateReducer/selectors";
import {selectIsParallelSupplyPhase} from "../../utils/gameState/supply/parallelSupplySelectors";
import {usePausableTimeout} from "../../hooks/react/usePausableTimeout";
import {useMatchToastsPaused} from "../MatchToastStack/matchToastsPause";
import {rememberToastHintHidden} from "../../utils/toast/toastHintsStorage";

const Toast = () => {
  const dispatch = useDispatch();
  const toast = useSelector(selectToastState);
  // Тост, который лежал в сторе ДО появления игрового экрана, к этой партии не
  // относится и не показывается. Стор один на все приложение, а снимают тост
  // только эффекты этого компонента — он же смонтирован лишь на экране матча
  // (GameContent). Поэтому все, что успело показать тост вне матча (витрина
  // главного меню проигрывает запись чужого матча со всеми ее эффектами, лобби,
  // ссылки-приглашения), оставалось висеть в сторе и всплывало в начале
  // следующего матча. Тем же приемом закрыт соседний случай в MatchPolicyToast.
  //
  // Сравнение по ссылке на объект: редьюсер возвращает НОВЫЙ объект на каждый
  // SHOW_TOAST, так что и два одинаковых сообщения подряд различимы
  const staleToastRef = useRef(toast.show ? toast : null);
  const isToastActive = toast.show && toast !== staleToastRef.current;
  // Пауза (на телефоне открыт список игроков или рынок): тоста не видно, его
  // время стоит, звук ждет показа. Снятие сменой хода работает и на паузе.
  // Предупреждение и ошибка на паузу не встают: это ответ на действие игрока прямо в этой
  // панели (купить политику в чужой ход), и позже он был бы уже не к месту
  const isPaused = useMatchToastsPaused() && !checkIfToastAnswersPlayerAction(toast.type);
  const isShown = isToastActive && !isPaused;
  const currentPlayer = useSelector(selectCurrentPlayer);
  const phase = useSelector(selectPhase);
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);

  const currentPlayerId = currentPlayer ? currentPlayer.id : null;

  // Ключ смены хода. В одновременной фазе снабжения текущий игрок заморожен
  // (см. utils/gameState/supply/parallelSupply) и может не смениться ни на
  // входе в фазу, ни при ее закрытии — смену хода там отмечает сам вход в
  // фазу и выход из нее. Вне этой фазы ключ — по-прежнему id текущего игрока
  const turnKey = isParallelSupplyPhase ? `${phase}|${currentPlayerId}` : currentPlayerId;

  const prevTurnKey = usePrev(turnKey);

  useEffect(() => {
    if (turnKey !== prevTurnKey && isToastActive && !toast.noHideOnTurnEnd) {
      dispatch(hideToast());
    }
  }, [dispatch, turnKey, prevTurnKey, isToastActive, toast.noHideOnTurnEnd]);

  usePausableTimeout(
    () => dispatch(hideToast()),
    toast.duration > 0 ? toast.duration : null,
    {isPaused, resetKey: toast, enabled: isToastActive}
  );

  // Звук — когда тост появился на экране или сменил текст и тип, пока виден.
  // Тост, уже прозвучавший до паузы, после нее не звучит повторно
  const prevIsShown = usePrev(isShown);
  const soundedRef = useRef({toast: null, message: null, type: null});
  useEffect(() => {
    if (!isShown || soundedRef.current.toast === toast) {
      return;
    }
    const sounded = soundedRef.current;
    const isContentChanged = sounded.message !== toast.message || sounded.type !== toast.type;
    soundedRef.current = {toast, message: toast.message, type: toast.type};
    if (!prevIsShown || isContentChanged) {
      getPlaySound(toast)();
    }
  }, [isShown, prevIsShown, toast]);


  if (!isShown) return null;

  const {hintId} = toast;

  return (
    <ToastView
      type={toast.type}
      icon={toast.icon}
      message={toast.message}
      onClose={() => dispatch(hideToast())}
      onHideHint={hintId ? () => {
        rememberToastHintHidden(hintId);
        dispatch(hideToast());
      } : undefined}
    />
  );
};

// Предупреждение и ошибка объясняют, что стало с действием игрока
const checkIfToastAnswersPlayerAction = (type) => type === TOAST_TYPES.WARNING || type === TOAST_TYPES.ERROR;

// Значки типов — рисунком, а не файлами: штрих цвета чернил язычка четкий на любом экране
const TYPE_GLYPHS = {
  [TOAST_TYPES.INFO]: <>
    <circle cx={12} cy={6} r={1.6} fill={'currentColor'} stroke={'none'}/>
    <path d={'M12 11v8'}/>
  </>,
  [TOAST_TYPES.SUCCESS]: <path d={'M5 12.5l4.6 4.6L19 7.5'}/>,
  [TOAST_TYPES.WARNING]: <>
    <path d={'M12 5v8.5'}/>
    <circle cx={12} cy={18.4} r={1.6} fill={'currentColor'} stroke={'none'}/>
  </>,
  [TOAST_TYPES.ERROR]: <path d={'M6.5 6.5l11 11M17.5 6.5l-11 11'}/>,
};

// Свои иконки событий — силуэты из общей папки, цвет дает маска
const EVENT_ICON_IMAGES = {
  [TOAST_ICONS.INITIATIVE]: '/img/icons/initiative white.png',
  [TOAST_ICONS.STEAL]: '/img/icons/steal_hand.png',
  [TOAST_ICONS.TRAP]: '/img/icons/trap_white.png',
  [TOAST_ICONS.COINS]: '/img/icons/coins white.png',
};

const ToastTabIcon = ({type, icon}) => {
  const eventImage = EVENT_ICON_IMAGES[icon];
  if (eventImage) {
    return <MaskedIcon className={'toast-tab_icon'} src={eventImage} color={'currentColor'}/>;
  }
  return (
    <svg
      className={'toast-tab_icon'}
      viewBox={'0 0 24 24'}
      fill={'none'}
      stroke={'currentColor'}
      strokeWidth={3.2}
      strokeLinecap={'round'}
      strokeLinejoin={'round'}
      aria-hidden={true}
    >
      {TYPE_GLYPHS[type] || TYPE_GLYPHS[TOAST_TYPES.INFO]}
    </svg>
  );
};

/**
 * Внешний вид тоста без логики показа — его же рисует витрина интерфейса
 * матча (AdminGameUiKitPage) во всех вариантах. Сверху слева из плиты выступает язычок
 * цвета типа со значком типа или своей иконкой события (icon, см. TOAST_ICONS).
 * onHideHint — у тоста-подсказки для новичков: под текстом ссылка, отключающая
 * подсказку насовсем
 */
export const ToastView = ({type, icon, message, onClose, onHideHint}) => (
  // Обертка — под тень и язычок: у плиты тоста срезаны углы (clip-path), и ее
  // собственная тень и все, что выступает за плиту, срезались бы. Не на стеке тостов:
  // внутри стека живет модалка тоста-реакции, а filter сделал бы стек ее контейнером
  <div className={`toast-shadow toast-shadow_${type}`}>
    <span className={'toast-tab'}>
      <ToastTabIcon type={type} icon={icon}/>
    </span>
    <div className={`toast-container toast-container_${type}`}>
      <div style={{flex: 1}}>
        <div className={'toast-message'}>
          {message}
        </div>
        {onHideHint && (
          <button className={'toast-hide-hint-button'} onClick={onHideHint}>
            Не показывать эту подсказку
          </button>
        )}
      </div>
      <button
        className={'toast-exit-button'}
        onClick={onClose}
        title="Закрыть"
      >
        <EngravedIcon src={'/img/icons/exit.png'} size={18} alt={'close'}/>
      </button>
    </div>
  </div>
);

const getPlaySound = (toast) => () => {
  if (toast.noSound) {
    return;
  }

  const soundManager = REGISTRY_SINGLETON.getSoundManager();
  switch (toast.type) {
    case TOAST_TYPES.INFO:
    case TOAST_TYPES.SUCCESS:
      soundManager.playToastInfo();
      break;
    case TOAST_TYPES.WARNING:
    case TOAST_TYPES.ERROR:
      soundManager.playToastError();
      break;
    default:
      soundManager.playToastInfo();
  }
}

export default Toast;
